<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'HSN Master Management' : 'Deleted HSN Masters'"
              :subtitle="isDeletePage() != true ? 'Manage HSN codes and their corresponding GST rates' : 'Restore or permanently delete HSN masters'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/hsn-masters/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add HSN Master
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter HSN Masters</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search HSN Masters"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="HSN code, description..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statusFilterOptions"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- GST Rate Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.gst_rate"
                    :items="gstRateOptions"
                    label="GST Rate"
                    prepend-inner-icon="mdi-percent"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined HSN Masters Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >HSN Masters List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-barcode-scan</v-icon>
                      {{ count }} {{ count === 1 ? "HSN Master" : "HSN Masters" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && hsnMasters.length > 0"
                      x-small
                      color="success"
                      outlined
                      @click="generateExcel"
                      class="px-2"
                      :loading="isExcelloading"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading HSN masters..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="hsnMasters"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- HSN Code -->
                <template v-slot:[`item.hsn_code`]="{ item }">
                  <v-chip
                    color="info"
                    x-small
                    outlined
                    class="font-weight-bold"
                  >
                    {{ item.hsn_code || 'N/A' }}
                  </v-chip>
                </template>

                <!-- Description -->
                <template v-slot:[`item.description`]="{ item }">
                  <div class="text-caption">
                    {{ truncateText(item.description, 50) || "No description" }}
                  </div>
                </template>

                <!-- GST Rate -->
                <template v-slot:[`item.gst_rate`]="{ item }">
                  <status-chip :color="getGSTRateColor(item.gst_rate)" icon="mdi-percent" :label="String(item.gst_rate !== null ? item.gst_rate + '%' : 'N/A')" />
                </template>

                <!-- Status -->
                <template v-slot:[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ item.is_active ? "mdi-check-circle" : "mdi-close-circle" }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Created Date -->
                <template v-slot:[`item.created_at`]="{ item }">
                  <div class="text-caption">
                    {{ formatDate(item.created_at) }}
                  </div>
                </template>

                <!-- Actions -->
                <template v-slot:[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/hsn-masters/${item.encrypted_id}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit HSN Master</span>
                      </v-tooltip>

                      <!-- Delete Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="showDeleteConfirmation(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete HSN Master</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="restoreItem(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-restore</v-icon>
                          </v-btn>
                        </template>
                        <span>Restore HSN Master</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- No Data -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-barcode-scan</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No HSN Masters Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No HSN masters match your current filters. Try adjusting your search criteria."
                          : "You haven't added any HSN masters yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/hsn-masters/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First HSN Master
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      style="max-width: 80px"
                      class="compact-input"
                      @keydown.enter="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Confirmation Dialog -->
      <delete-confirmation-dialog
        v-model="deleteDialog"
        :item-name="selectedItem?.hsn_code || 'this HSN Master'"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </v-container>
  </v-main>
</template>

<script>
import { mapState } from "vuex";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
import axios from "@/axios";

export default {
  name: "HsnMasterIndex",
  components: {
    DeleteConfirmationDialog,
  },
  data() {
    return {
      isLoading: false,
      hsnMasters: [],
      count: 0,
      deleteDialog: false,
      selectedItem: null,
      isExcelloading: false,
      jumpToPage: 1,

      // Pagination
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,

      // Filters
      filter: {
        search_keyword: "",
        is_active: null,
        gst_rate: null,
      },

      // Table Headers
      headers: [
        {
          text: "Sr.",
          align: "center",
          sortable: false,
          value: "sr_no",
          width: "70px",
        },
        {
          text: "HSN Code",
          align: "start",
          sortable: true,
          value: "hsn_code",
          width: "150px",
        },
        {
          text: "Description",
          align: "start",
          sortable: true,
          value: "description",
          width: "40%",
        },
        {
          text: "GST Rate",
          align: "center",
          sortable: true,
          value: "gst_rate",
          width: "120px",
        },
        {
          text: "Status",
          align: "center",
          sortable: true,
          value: "is_active",
          width: "100px",
        },
        {
          text: "Created Date",
          align: "center",
          sortable: true,
          value: "created_at",
          width: "150px",
        },
        {
          text: "Actions",
          align: "center",
          sortable: false,
          value: "actions",
          width: "120px",
        },
      ],

      statusFilterOptions: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],

      gstRateOptions: [
        { text: "0% - Exempt", value: 0 },
        { text: "3% - Gold", value: 3 },
        { text: "5% - Essential", value: 5 },
        { text: "12% - Processed food", value: 12 },
        { text: "18% - Standard", value: 18 },
        { text: "28% - Luxury", value: 28 },
      ],
    };
  },

  computed: {
    ...mapState(["user"]),

    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active !== null ||
        this.filter.gst_rate !== null
      );
    },

    paginationLength() {
      return Math.ceil(this.count / this.itemsPerPage);
    },
  },

  watch: {
    page() {
      this.getData();
    },

    "filter.search_keyword"() {
      this.debounceSearch();
    },

    selectedItemsPerPage(newVal) {
      this.itemsPerPage = newVal;
      this.page = 1;
      this.getData();
    },
  },

  mounted() {
    this.getData();
  },

  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          show_deleted: this.isDeletePage(),
          ...this.filter,
        };

        // Remove null/undefined values
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === undefined || params[key] === "") {
            delete params[key];
          }
        });

        const response = await axios.get("/hsn-masters", { params });
        this.hsnMasters = response.data.data || [];
        this.count = response.data.count || 0;
      } catch (error) {
        console.error("Error fetching HSN Masters:", error);
        this.$toast.error("Failed to fetch HSN Masters");
      } finally {
        this.isLoading = false;
      }
    },

    showDeleteConfirmation(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.selectedItem) return;

      try {
        await axios.post(`/hsn-masters/delete/${this.selectedItem.encrypted_id}`);
        this.$toast.success("HSN Master deleted successfully");
        this.getData();
      } catch (error) {
        console.error("Error deleting HSN Master:", error);
        this.$toast.error("Failed to delete HSN Master");
      } finally {
        this.deleteDialog = false;
        this.selectedItem = null;
      }
    },

    cancelDelete() {
      this.deleteDialog = false;
      this.selectedItem = null;
    },

    async restoreItem(item) {
      try {
        await axios.post(`/hsn-masters/restore/${item.encrypted_id}`);
        this.$toast.success("HSN Master restored successfully");
        this.getData();
      } catch (error) {
        console.error("Error restoring HSN Master:", error);
        this.$toast.error("Failed to restore HSN Master");
      }
    },

    isDeletePage() {
      return this.$route.query.deleted === "true";
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active !== null) count++;
      if (this.filter.gst_rate !== null) count++;
      return count;
    },

    clearAllFilters() {
      this.filter = {
        search_keyword: "",
        is_active: null,
        gst_rate: null,
      };
      this.page = 1;
      this.getData();
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.getData();
      }, 500);
    },

    changeItemsPerPageToggle(value) {
      this.selectedItemsPerPage = value;
    },

    goToPage() {
      if (this.jumpToPage >= 1 && this.jumpToPage <= this.paginationLength) {
        this.page = parseInt(this.jumpToPage);
      }
    },

    async generateExcel() {
      this.isExcelloading = true;
      try {
        const response = await axios.get("/hsn-masters/excel", {
          params: this.filter,
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `hsn_masters_${new Date().getTime()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        this.$toast.success("HSN Masters exported successfully");
      } catch (error) {
        console.error("Error exporting HSN Masters:", error);
        this.$toast.error("Failed to export HSN Masters");
      } finally {
        this.isExcelloading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-IN");
    },

    getGSTRateColor(rate) {
      if (rate === 0) return "grey";
      if (rate <= 5) return "green";
      if (rate <= 12) return "blue";
      if (rate <= 18) return "orange";
      if (rate <= 28) return "red";
      return "purple";
    },

    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
  },
};
</script>

<style scoped>
.enhanced-table {
  border-collapse: separate;
  border-spacing: 0;
}

.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 0;
}

.enhanced-table >>> thead tr th {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600 !important;
  color: #424242 !important;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f9f9f9 !important;
}

.enhanced-table >>> tbody tr td {
  border-bottom: 1px solid #f0f0f0;
}

.compact-pagination >>> .v-pagination__navigation,
.compact-pagination >>> .v-pagination__item {
  min-width: 30px;
  height: 30px;
  margin: 0 2px;
}

.compact-input >>> .v-input__control {
  min-height: 32px !important;
}

.compact-input >>> .v-text-field__details {
  display: none !important;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;
}
</style>